import { InputHTMLAttributes, forwardRef, ReactNode } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/16/solid';
import { cn } from '@/lib/utils';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Mensaje de error - muestra borde rojo e icono de error */
  error?: string;
  /** Label del input */
  label?: string;
  /** Texto de ayuda debajo del input */
  helpText?: string;
  /** Icono a mostrar a la izquierda del input */
  leadingIcon?: ReactNode;
}

/**
 * Input - Campo de texto con estilos Tailwind Plus
 * 
 * Variantes soportadas:
 * - Input básico con label
 * - Input con help text
 * - Input con error de validación (icono + mensaje)
 * - Input con estado disabled
 * - Input con leading icon
 * 
 * @example
 * ```tsx
 * // Básico con label
 * <Input label="Email" placeholder="you@example.com" />
 * 
 * // Con help text
 * <Input label="Email" helpText="We'll only use this for spam." />
 * 
 * // Con error
 * <Input label="Email" error="Not a valid email address." />
 * 
 * // Con icono
 * <Input label="Email" leadingIcon={<EnvelopeIcon />} />
 * 
 * // Disabled
 * <Input label="Email" disabled defaultValue="you@example.com" />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ error, label, helpText, leadingIcon, className = '', id, disabled, ...props }, ref) => {
    const inputId = id || `input-${Math.random().toString(36).substring(2, 9)}`;
    const hasLeadingIcon = !!leadingIcon;
    const hasError = !!error;
    
    // Determinar el aria-describedby basado en error o helpText
    const describedBy = hasError 
      ? `${inputId}-error` 
      : helpText 
        ? `${inputId}-description` 
        : undefined;

    return (
      <div>
        {label && (
          <label 
            htmlFor={inputId} 
            className="block text-sm/6 font-medium text-secondary-900 dark:text-white"
          >
            {label}
          </label>
        )}
        <div className={cn(label && 'mt-2', 'grid grid-cols-1')}>
          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            className={cn(
              // Grid positioning
              'col-start-1 row-start-1',
              // Base
              'block w-full rounded-md bg-white px-3 py-1.5 text-base text-secondary-900 sm:text-sm/6',
              // Padding ajustado para iconos
              hasLeadingIcon && 'pl-10 sm:pl-9',
              hasError && 'pr-10 sm:pr-9',
              // Normal state - Light mode (usando border en lugar de outline)
              !hasError && [
                'border border-secondary-300',
                'placeholder:text-secondary-400',
                'focus:border-primary-500 focus:ring-1 focus:ring-primary-500',
              ],
              // Error state - Light mode
              hasError && [
                'text-danger-900 border border-danger-300',
                'placeholder:text-danger-300',
                'focus:border-danger-500 focus:ring-1 focus:ring-danger-500',
              ],
              // Normal state - Dark mode
              !hasError && [
                'dark:bg-white/5 dark:text-white dark:border-white/10',
                'dark:placeholder:text-secondary-500',
                'dark:focus:border-primary-500 dark:focus:ring-primary-500',
              ],
              // Error state - Dark mode  
              hasError && [
                'dark:bg-danger-950/20 dark:text-danger-300 dark:border-danger-500/50',
                'dark:placeholder:text-danger-400',
                'dark:focus:border-danger-500 dark:focus:ring-danger-500',
              ],
              // Disabled state
              'disabled:cursor-not-allowed disabled:bg-secondary-50 disabled:text-secondary-500 disabled:border-secondary-200',
              'dark:disabled:bg-white/[0.02] dark:disabled:text-secondary-500 dark:disabled:border-white/5',
              // Remove default focus outline
              'focus:outline-none',
              className
            )}
            aria-invalid={hasError ? 'true' : undefined}
            aria-describedby={describedBy}
            {...props}
          />
          
          {/* Leading icon */}
          {hasLeadingIcon && (
            <div 
              className={cn(
                'pointer-events-none col-start-1 row-start-1 ml-3 flex items-center self-center',
                hasError 
                  ? 'text-danger-500 dark:text-danger-400' 
                  : 'text-secondary-400 dark:text-secondary-500'
              )}
              aria-hidden="true"
            >
              <span className="size-5 sm:size-4">
                {leadingIcon}
              </span>
            </div>
          )}
          
          {/* Error icon */}
          {hasError && (
            <ExclamationCircleIcon
              aria-hidden="true"
              className="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-danger-500 sm:size-4 dark:text-danger-400"
            />
          )}
        </div>
        
        {/* Help text */}
        {helpText && !hasError && (
          <p 
            id={`${inputId}-description`} 
            className="mt-2 text-sm text-secondary-500 dark:text-secondary-400"
          >
            {helpText}
          </p>
        )}
        
        {/* Error message */}
        {hasError && (
          <p 
            id={`${inputId}-error`} 
            className="mt-2 text-sm text-danger-600 dark:text-danger-400" 
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
